/* eslint-disable max-len */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import './AddNewApiModal.scss';
import { Button, Form, InputGroup } from 'react-bootstrap';
import {
  apiKeyRequest
} from '../../store/apiKey/apiKeyActions';
import { 
  Key, 
  Lock, 
  Save, 
  AlertCircle, 
  CheckCircle, 
  Eye, 
  EyeOff,
  Shield,
  Info
} from 'lucide-react';

function AddNewApi({handleClose, ...props }) {
  const {
    apiKeyRequest,
  } = props;

  const apiKeyRegex = /^[a-zA-Z0-9]{64}$/;
  const apiSecretRegex = /^[a-zA-Z0-9!@#$%^&*()_+[\]{};':"\\|,.<>\/?`~\-]{32,64}$/;

  const [errors, setErrors] = useState({ apiKey: '', apiSecret: '' });
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateInputs = () => {
    const newErrors = { apiKey: '', apiSecret: '' };
    if (!apiKeyRegex.test(apiKey)) {
      newErrors.apiKey = 'API Key must be 64 alphanumeric characters.';
    }
    if (!apiSecretRegex.test(apiSecret)) {
      newErrors.apiSecret = 'API Secret must be 32-64 characters, including special characters.';
    }
    setErrors(newErrors);
    return !newErrors.apiKey && !newErrors.apiSecret;
  };

  const addApiKey = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;
    
    setIsLoading(true);
    try {
      const payload = {
        api_key: apiKey,
        api_secret: apiSecret,
        exchange: 'binance',
      };
      await apiKeyRequest(payload);
      handleClose();
    } catch (err) {
      console.error('API Key addition failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modern-api-form">
      {/* Security Notice */}
      <div className="security-notice">
        <Shield size={20} className="notice-icon" />
        <div className="notice-content">
          <h6>Secure Connection</h6>
          <p>Your API credentials are encrypted and stored securely</p>
        </div>
      </div>

      <form onSubmit={addApiKey} className="api-form">
        {/* API Key Field */}
        <div className="form-group">
          <label className="form-label">
            <Key size={16} />
            API Key
          </label>
          <div className="input-wrapper">
            <InputGroup className="modern-input-group">
              <Form.Control
                type="text"
                placeholder="Enter your Binance API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                isInvalid={!!errors.apiKey}
                className="modern-form-control"
              />
              {apiKey && !errors.apiKey && (
                <div className="input-success-icon">
                  <CheckCircle size={16} />
                </div>
              )}
            </InputGroup>
            {errors.apiKey && (
              <div className="error-message">
                <AlertCircle size={14} />
                <span>{errors.apiKey}</span>
              </div>
            )}
          </div>
        </div>

        {/* API Secret Field */}
        <div className="form-group">
          <label className="form-label">
            <Lock size={16} />
            API Secret
          </label>
          <div className="input-wrapper">
            <InputGroup className="modern-input-group">
              <Form.Control
                type={showApiSecret ? "text" : "password"}
                placeholder="Enter your Binance API Secret"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                isInvalid={!!errors.apiSecret}
                className="modern-form-control"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowApiSecret(!showApiSecret)}
              >
                {showApiSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {apiSecret && !errors.apiSecret && (
                <div className="input-success-icon">
                  <CheckCircle size={16} />
                </div>
              )}
            </InputGroup>
            {errors.apiSecret && (
              <div className="error-message">
                <AlertCircle size={14} />
                <span>{errors.apiSecret}</span>
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="info-box">
          <Info size={16} className="info-icon" />
          <div className="info-content">
            <h6>API Permissions Required:</h6>
            <ul>
              <li>Read account information</li>
              <li>Place and cancel orders</li>
              <li>Access trading history</li>
            </ul>
            <p className="info-note">
              Make sure to enable these permissions when creating your API key in Binance.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="cancel-btn"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="save-btn"
            disabled={isLoading || !apiKey || !apiSecret}
          >
            <Save size={16} />
            {isLoading ? 'Saving...' : 'Save API Key'}
          </Button>
        </div>
      </form>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    // Add any required state here
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    apiKeyRequest: (creds) => {
      dispatch(apiKeyRequest(creds));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddNewApi);